// M5-R10 (docs/build-briefs/M5-R10-price-change-history-screen.md; DEC-355):
// the wording and order of a drink's price history, with no DOM so the rules
// are unit tested. Every figure and every flag is the server's, decided from
// each ledger row's own snapshot; this only formats them. Oldest first, the
// last card is "Now", a put-back is its own card, and a field the row never
// recorded reads N/A, never a guess.

import type { LinePriceHistoryDto, LinePriceHistoryRowDto } from "./api.ts";
import { formatCents } from "./money.ts";

export const NOT_AVAILABLE = "-";

/** "+$2.00" or the minus-sign form "−$3.00" the designer's mockup uses. */
export function signedCents(cents: number): string {
  return cents < 0 ? `−${formatCents(-cents)}` : `+${formatCents(cents)}`;
}

/** "1 change", "4 changes": the line menu row's right-hand label. */
export function changeCountLabel(count: number): string {
  return count === 1 ? "1 change" : `${count} changes`;
}

export type HistoryTagKind = "half-off" | "below-cost" | "went-up";

export interface HistoryTag {
  kind: HistoryTagKind;
  text: string;
}

export interface HistoryCardView {
  number: number;
  time: string;
  isPutBack: boolean;
  isNow: boolean;
  fromText: string;
  toText: string;
  /** Signed chip, the new price against the one before it. */
  diffText: string;
  /** "Set by", or "Put back by" on a put-back card. */
  actorLabel: string;
  actor: string;
  approver: string;
  reason: string;
  tags: HistoryTag[];
}

export interface HistorySummaryView {
  menuText: string;
  nowText: string;
  /** The signed difference of Now against the menu price, "" when equal. */
  nowDiffText: string;
  changesText: string;
  /** "1 put back", "" when none. */
  putBackText: string;
}

export interface PriceHistoryView {
  /** True when the line has no rows: the sheet reads N/A. */
  empty: boolean;
  summary: HistorySummaryView | null;
  cards: HistoryCardView[];
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function orNotAvailable(value: string | null): string {
  return value === null || value.trim() === "" ? NOT_AVAILABLE : value;
}

function tagsFor(row: LinePriceHistoryRowDto): HistoryTag[] {
  const tags: HistoryTag[] = [];
  if (row.halfOffOrMore) {
    tags.push({ kind: "half-off", text: "Half off or more" });
  }
  if (row.belowCost && row.drinkCostCents !== null) {
    const how = row.drinkCostBasis === "lower_bound" ? "at least" : "about";
    tags.push({
      kind: "below-cost",
      text: `Below cost, ingredients ${how} ${formatCents(row.drinkCostCents)}`,
    });
  }
  if (row.wentUp) {
    tags.push({ kind: "went-up", text: "Price went up" });
  }
  return tags;
}

/** The whole sheet body, from the server's history. `formatTime` is a seam for
 * tests; the default is the browser's own short time, as the closeout uses. */
export function buildPriceHistoryView(
  history: LinePriceHistoryDto,
  formatTime: (iso: string) => string = timeOf,
): PriceHistoryView {
  const rows = history.rows;
  if (rows.length === 0 || history.menuPriceCents === null || history.nowPriceCents === null) {
    return { empty: true, summary: null, cards: [] };
  }
  const putBacks = rows.filter((row) => row.kind === "restore").length;
  const menu = history.menuPriceCents;
  const now = history.nowPriceCents;
  const cards = rows.map((row, index): HistoryCardView => {
    const isPutBack = row.kind === "restore";
    return {
      number: index + 1,
      time: formatTime(row.at),
      isPutBack,
      isNow: index === rows.length - 1,
      fromText: formatCents(row.previousPriceCents),
      toText: formatCents(row.newPriceCents),
      diffText: signedCents(row.differenceCents),
      actorLabel: isPutBack ? "Put back by" : "Set by",
      actor: orNotAvailable(row.setBy),
      approver: orNotAvailable(row.approvedBy),
      reason: orNotAvailable(row.reason),
      tags: tagsFor(row),
    };
  });
  return {
    empty: false,
    summary: {
      menuText: formatCents(menu),
      nowText: formatCents(now),
      nowDiffText: now === menu ? "" : signedCents(now - menu),
      changesText: String(rows.length),
      putBackText: putBacks === 0 ? "" : `${putBacks} put back`,
    },
    cards,
  };
}

/** "2026-10-02" -> "Friday, October 2, 2026", the closeout sheet's subtitle.
 * Read as a calendar date in UTC so the viewer's time zone never moves the day. */
export function businessDateLabel(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (match === null) {
    return isoDate;
  }
  const stamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(stamp).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
